import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Pages
import Login from "./pages/Login";
import VerifyCode from "./pages/VerifyCode";
import EmailLogin from "./pages/EmailLogin";
import RecoveryRequestPage from "./pages/RecoveryRequestPage";
import InvestorAccessRequest from "./pages/InvestorAccessRequest";
import StartApplication from "./pages/StartApplication";
import CompetitorVideoPage from "./pages/CompetitorVideoPage";
import PortalSelection from "./pages/PortalSelection";
import SharedEntry from "./pages/SharedEntry";
import AuthStatusPage from "./pages/AuthStatusPage";
import { getValidatedBrowserSession } from "./utils/session/browserSession";
import AuthShell from "./components/auth/AuthShell";
import { applyLightLoginTheme } from "./utils/ui/lightLoginTheme";

const PROTECTED_PATHS = new Set();

function AuthIntentThemeSync() {
  const location = useLocation();

  React.useLayoutEffect(() => {
    applyLightLoginTheme();
  }, [location]);

  return null;
}

export function SessionGuard({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const requiresSession = PROTECTED_PATHS.has(location.pathname);
  const routeKey = `${location.pathname}${location.search || ""}`;
  const [sessionState, setSessionState] = React.useState({
    checked: false,
    session: null,
    routeKey: null,
  });

  React.useEffect(() => {
    let isMounted = true;

    if (!requiresSession) {
      setSessionState((current) => (
        current.checked && current.routeKey === routeKey
          ? current
          : { ...current, checked: true, routeKey }
      ));

      return () => {
        isMounted = false;
      };
    }

    setSessionState((current) => ({ checked: false, session: current.session, routeKey }));

    async function validateSession() {
      try {
        const session = await getValidatedBrowserSession();

        if (isMounted) {
          setSessionState({ checked: true, session, routeKey });
        }
      } catch (_error) {
        if (isMounted) {
          setSessionState({ checked: true, session: null, routeKey });
        }
      }
    }

    validateSession();

    return () => {
      isMounted = false;
    };
  }, [location.pathname, location.search, requiresSession, routeKey]);

  React.useEffect(() => {
    const hasCurrentRouteValidation = sessionState.routeKey === routeKey;

    if (!sessionState.checked || !hasCurrentRouteValidation) {
      return;
    }

    if (!sessionState.session && requiresSession) {
      navigate(`/login${location.search || ""}`, { replace: true });
    }
  }, [location.search, navigate, requiresSession, routeKey, sessionState.checked, sessionState.routeKey, sessionState.session]);

  if (!sessionState.checked && requiresSession) {
    return (
      <AuthShell
        eyebrow="Secure access"
        title="Checking your session"
        description="We are validating your current sign-in before loading the next step."
        align="center"
      />
    );
  }

  return children;
}

const XPORTAL_LOGIN_CONTEXT = Object.freeze({
  intent: "broker",
  source: "xportal",
  returnTo: "/dashboard",
  hasExplicitIntent: true,
  isValidIntent: true,
  rawIntent: "broker",
});

export function XPortalLoginRoute() {
  return <Login launchContextOverride={XPORTAL_LOGIN_CONTEXT} />;
}

function App() {
  return (
    <Router>
      <AuthIntentThemeSync />
      <ToastContainer theme="light" />
      <SessionGuard>
        <Routes>
          <Route path="/" element={<StartApplication />} />
          <Route path="/start" element={<StartApplication />} />
          <Route path="/entry" element={<SharedEntry />} />
          <Route path="/xportal" element={<XPortalLoginRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/verify-code" element={<VerifyCode />} />
          <Route path="/auth/login-email" element={<EmailLogin />} />
          <Route path="/auth/recovery" element={<RecoveryRequestPage />} />
          <Route path="/investor-access-request" element={<InvestorAccessRequest />} />
          <Route path="/auth-status" element={<AuthStatusPage />} />
          <Route path="/portal-selection" element={<PortalSelection />} />
          <Route path="/video" element={<CompetitorVideoPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </SessionGuard>
    </Router>
  );
}

export default App;
